import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, concatMap, take } from 'rxjs/operators';

import { WidgetsService } from 'components/organisms/widgets-loader/widgets.service';
import { WIDGETS_CONFIG } from 'components/widgets/widgets-config';
import { HTTP_ERROR_HANDLER_SERVICE } from 'src/services/http-error-handler/entities/const';
import type { IHttpErrorHandlerService } from 'src/services/http-error-handler/entities/types';
import { TypicalProjectsRestService } from 'src/services/typical-projects-rest/typical-projects-rest.service';

import { BaseResolver } from './base-resolver';
import type { IDefaultLayout, ILandingPage } from '../layouts/entities/types';

// See https://www.wrike.com/open.htm?id=1408653453 for details
@Injectable()
export class PilotResolverService extends BaseResolver implements Resolve<ILandingPage | IDefaultLayout> {
    protected readonly httpErrorHandlerService: IHttpErrorHandlerService;
    private readonly restService = inject(TypicalProjectsRestService);
    private readonly widgetsService = inject(WidgetsService);

    constructor() {
        const httpErrorHandlerService = inject<IHttpErrorHandlerService>(HTTP_ERROR_HANDLER_SERVICE);

        super(httpErrorHandlerService);

        this.httpErrorHandlerService = httpErrorHandlerService;
    }

    public resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<ILandingPage | IDefaultLayout> {
        return this.makePageRequest(route).pipe(
            take(1),
            concatMap((data) => this.handleSuccess(data)),
            concatMap((data) => this.widgetsService.resolveWidgetsFromPage(data, WIDGETS_CONFIG)),
            catchError((error) => this.handleError(error))
        );
    }

    private makePageRequest(route: ActivatedRouteSnapshot) {
        return this.restService.getProjectRootPage(route.routeConfig.path);
    }
}
